import { html, type TemplateResult } from "lit";

// Provider marks, inline.
//
// A "Sign in with Google" button without the Google mark reads as a link, not a
// button people trust with their identity — recognition is most of what makes
// these work. Inlined rather than fetched because the sign-in page must render
// completely on a cold load, and a logo that arrives late makes the primary
// action of the page flicker.
//
// Drawn from each vendor's published brand guidance. Unknown providers get a
// neutral key glyph rather than nothing, so a customer's own OIDC still looks
// like a real button.

const GOOGLE = html`
  <svg viewBox="0 0 48 48" aria-hidden="true" width="18" height="18">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.2 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
`;

const MICROSOFT = html`
  <svg viewBox="0 0 23 23" aria-hidden="true" width="17" height="17">
    <path fill="#f25022" d="M1 1h10v10H1z" />
    <path fill="#7fba00" d="M12 1h10v10H12z" />
    <path fill="#00a4ef" d="M1 12h10v10H1z" />
    <path fill="#ffb900" d="M12 12h10v10H12z" />
  </svg>
`;

const APPLE = html`
  <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18">
    <path
      fill="currentColor"
      d="M16.36 12.78c-.02-2.4 1.96-3.55 2.05-3.61-1.12-1.63-2.86-1.86-3.48-1.89-1.48-.15-2.89.87-3.64.87-.75 0-1.91-.85-3.14-.83-1.61.02-3.1.94-3.93 2.38-1.68 2.91-.43 7.22 1.2 9.58.8 1.16 1.75 2.46 3 2.41 1.21-.05 1.66-.78 3.12-.78 1.46 0 1.87.78 3.14.76 1.3-.02 2.12-1.18 2.91-2.34.92-1.34 1.3-2.64 1.32-2.71-.03-.01-2.53-.97-2.55-3.84zM14.0 5.6c.66-.8 1.11-1.92.99-3.03-.95.04-2.11.63-2.79 1.43-.61.71-1.15 1.85-1.01 2.94 1.06.08 2.15-.54 2.81-1.34z"
    />
  </svg>
`;

const GITHUB = html`
  <svg viewBox="0 0 16 16" aria-hidden="true" width="18" height="18">
    <path
      fill="currentColor"
      d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0016 8c0-4.42-3.58-8-8-8z"
    />
  </svg>
`;

const GENERIC = html`
  <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18">
    <path
      fill="currentColor"
      d="M12.65 10A5.99 5.99 0 007 6a6 6 0 100 12 5.99 5.99 0 005.65-4H17v4h4v-4h2v-4H12.65zM7 14a2 2 0 110-4 2 2 0 010 4z"
    />
  </svg>
`;

export function providerMark(name: string): TemplateResult {
  switch (name) {
    case "google":
      return GOOGLE;
    case "microsoft":
      return MICROSOFT;
    case "apple":
      return APPLE;
    case "github":
      return GITHUB;
    default:
      return GENERIC;
  }
}
